import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import { jwtDecode } from 'jwt-decode';
import {
  getCookie,
  setCookie,
  deleteCookie,
} from '@/utils';
import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import {
  apiClientUrl,
  datadogApplicationId,
  datadogClientToken,
  datadogService,
} from '@/config';

const userStore = defineStore('userStore', () => {
  const state = reactive({
    init: false,
    accessToken: '',
    status: '',
    scope: [],
    exp: 0,
    id: '',
    tokenExpired: false,
    mode: 'MC',
  });

  const timeZone = ref('UTC');
  const timeZoneList = reactive([
    {
      type: 'UTC',
      offset: 0,
      text: '(UTC)',
    },
    {
      type: 'KST',
      offset: -540,
      text: '(KST)',
    },
  ]);

  const inIframe = window.self !== window.top;

  const storage = {
    cookie: {
      get: ({ key }) => getCookie(key),
      set: ({ key, value }) => {
        setCookie(key, value);
      },
      delete: ({ key }) => {
        deleteCookie(key);
      },
    },
    sessionStorage: {
      get: ({ key }) => sessionStorage.getItem(key),
      set: ({ key, value }) => {
        sessionStorage.setItem(key, value);
      },
      delete: ({ key }) => {
        sessionStorage.removeItem(key);
      },
    },
  };

  const {
    get: getStorage,
    set: setStorage,
    delete: deleteStorage,
  } = inIframe ? storage.sessionStorage : storage.cookie;

  const reset = () => {
    state.scope = [];
    state.exp = 0;
    state.tokenExpired = false;
    state.id = '';
    state.status = '';
    state.accessToken = '';
  };

  const payloadToState = (payload) => {
    switch (payload.user_scope) {
      case '1012':
        state.status = 'MFA';
        break;
      case '1022':
        state.status = 'TEMPORARY_PASSWORD';
        break;
      default:
        break;
    }

    state.scope = payload.user_scope.split(',');
    state.exp = payload.exp;
    state.tokenExpired = state.exp < (Date.now() / 1000);
    state.id = payload.username;

    if (['1140', '1141'].some((v) => state.scope.includes(v))) {
      state.mode = 'MC';
    }

    if (['1240', '1241'].some((v) => state.scope.includes(v))) {
      state.mode = 'CORE';
    }

    // account type
    const type = payload?.orgType || '';

    switch (type) {
      case 'SYSTEM_SFMC':
        state.mode = 'MC';
        break;
      case 'SYSTEM_CORE':
        state.mode = 'CORE';
        break;
      default:
        break;
    }
  };

  const setToken = async ({ accessToken, idToken }) => {
    reset();

    try {
      const payload = jwtDecode(accessToken);

      payloadToState(payload);
      state.accessToken = accessToken;
    } catch (error) {
      return;
    }

    setStorage({ key: 'accessToken', value: accessToken });
    setStorage({ key: 'idToken', value: idToken });
  };

  const refreshAccessToken = async () => {
    try {
      const {
        data: {
          accessToken,
          idToken,
        },
      } = await axios.post(`${apiClientUrl}/token`, null, {
        withCredentials: true,
      });

      setToken({ accessToken, idToken });
    } catch (error) {
      //
    } finally {
      state.init = true;
    }
  };

  const init = () => {
    state.init = false;

    if (datadogApplicationId) {
      datadogRum.init({
        applicationId: datadogApplicationId,
        clientToken: datadogClientToken,
        site: 'datadoghq.com',
        service: datadogService,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        defaultPrivacyLevel: 'allow',
        sessionPersistence: 'local-storage',
        allowedTracingUrls: [
          'https://partner.api.i2message.io',
        ],
      });
    }

    try {
      const accessToken = getStorage({ key: 'accessToken' });
      state.accessToken = accessToken;

      const payload = jwtDecode(accessToken);

      payloadToState(payload);

      if (datadogApplicationId) {
        // datadog
        datadogRum.onReady(() => {
          datadogRum.startSessionReplayRecording({ force: true });

          datadogRum.setUser({
            id: payload.account_id,
            username: payload.username,
          });
        });
      }
    } catch (error) {
      //
    } finally {
      if (!state.accessToken || state.tokenExpired) {
        refreshAccessToken();
      } else {
        state.init = true;
      }
    }
  };

  const clear = () => {
    deleteStorage({ key: 'accessToken' });
    deleteStorage({ key: 'idToken' });

    reset();
  };

  init();

  const timezoneOffset = new Date().getTimezoneOffset();
  const symbol = timezoneOffset < 0 ? '+' : '-';
  const hour = Math.floor(Math.abs(timezoneOffset) / 60).toString().padStart(2, '0');
  const minutes = (Math.abs(timezoneOffset) % 60).toString().padStart(2, '0');

  if (!timeZoneList.some(({ offset }) => offset === timezoneOffset)) {
    timeZoneList.push({
      type: 'locale',
      offset: timezoneOffset,
      text: `(UTC${symbol}${hour}:${minutes})`,
    });
  }

  const localStorageTimeZone = localStorage.getItem('timeZone');

  if (!timeZoneList.some(({ type }) => {
    if (type === localStorageTimeZone) {
      timeZone.value = localStorageTimeZone;
      return true;
    }

    return false;
  })) {
    timeZone.value = timeZoneList[0].type;
    localStorage.setItem('timeZone', timeZone.value);
  }

  const nextTimeZone = () => {
    const i = timeZoneList.findIndex(({ type }) => type === timeZone.value);
    const index = (i + 1) % timeZoneList.length;

    timeZone.value = timeZoneList[index].type;
    localStorage.setItem('timeZone', timeZone.value);
  };

  return {
    state,
    setToken,
    clear,
    timeZone,
    timeZoneList,
    nextTimeZone,
  };
});

export default userStore;
